import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ApplicationProvider } from './contexts/ApplicationContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ChatProvider } from './contexts/ChatContext';
import { Navigation, RouteErrorBoundary, AppCrashPage } from './components/ui';
import { GlobalChatOverlay } from './components/chat/GlobalChatOverlay';
import AnalyticsBootstrapper from './components/analytics/AnalyticsBootstrapper';
import VoloceanLoader from './components/loading/VoloceanLoader';
import { MarketingHeroSuspenseFallback } from './components/volocean/MarketingHeroSuspenseFallback';
import { VOLOCEAN_ASSETS } from './types/volocean.types';
import { getRouteSkeletonFallback } from './config/routeSkeletons';
import { RedirectClientsDeliveryMode, RedirectInvestorsSyndicatesTab } from './utils/admin/delivery/deliveryListNavigation';
import { shouldShowNav, shouldShowGlobalChat } from './config/layout';
import { ClientSpecificRoute } from './components/routing/ClientSpecificRoute';
import { StandaloneCodeGate } from './components/routing/StandaloneCodeGate';
import { STANDALONE_PAGE_ACCESS } from './config/standaloneAccess';
import {
  ALFRED_AI_GUIDE_ALLOWED_DOMAINS,
  ALFRED_AI_GUIDE_ALLOWED_EMAILS,
  ALFRED_AI_GUIDE_PATH,
  canAccessAlfredAiTargetGuide,
  getApprovedClientHomePath,
  isPathAllowedForAlfredGuideOnly,
} from './constants/alfredAiGuideAccess';
import { isAdminRole, isProspectViewerRole, isStaffViewerEmail, isTargetViewerRole } from './utils/authRoles';
import {
  resolveTargetViewerProtectedRedirect,
  resolveTargetViewerPublicRedirect,
  TARGET_VIEWER_HOME_PATH,
  getRoleHomePath,
} from './utils/targetViewerRoutes';
import {
  isProspectViewerAllowedPath,
  resolveProspectViewerProtectedRedirect,
  resolveProspectViewerPublicRedirect,
  PROSPECT_VIEWER_HOME_PATH,
} from './utils/prospectViewerRoutes';
import { applyDemoDocumentTitle } from './utils/demoEmulator';

// Global Scroll Restoration Component
const ScrollToTop: React.FC = () => {
  const { pathname, search, key } = useLocation();

  useEffect(() => {
    // Prevent browser history from restoring previous scroll positions on navigation.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const forceTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    // Run multiple times to override late layout/focus-induced jumps.
    forceTop();
    const rafId = window.requestAnimationFrame(forceTop);
    const timeoutId = window.setTimeout(forceTop, 80);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [pathname, search, key]);

  return null;
};

// Lazy loaded pages - loaded on demand
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const AuthClaimPage = lazy(() => import('./pages/AuthClaimPage'));
const AuthViewerClaimPage = lazy(() => import('./pages/AuthViewerClaimPage'));
const AuthProspectClaimPage = lazy(() => import('./pages/AuthProspectClaimPage'));
const TargetViewerTargetsPage = lazy(() => import('./pages/TargetViewerTargetsPage'));
const ProspectViewerProspectsPage = lazy(() => import('./pages/ProspectViewerProspectsPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const WaitingForApproval = lazy(() => import('./pages/WaitingForApproval'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const ClientProfilePage = lazy(() => import('./pages/ClientProfilePage'));
const AdminClientsPage = lazy(() => import('./pages/AdminClientsPage'));
const AdminClientDetailPage = lazy(() => import('./pages/AdminClientDetailPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminAuditLogsPage = lazy(() => import('./pages/AdminAuditLogsPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/AdminAnalyticsPage'));
const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage'));
const AdminInvestorsPage = lazy(() => import('./pages/AdminInvestorsPage'));
const AdminCampaignsPage = lazy(() => import('./pages/AdminCampaignsPage'));
const AdminLeadsPage = lazy(() => import('./pages/AdminLeadsPage'));
const AdminVolOceanPage = lazy(() => import('./pages/AdminVolOceanPage'));
const AdminPublicPagesHub = lazy(() => import('./pages/AdminPublicPagesHub'));
const ApplicationManualHubPage = lazy(() => import('./pages/ApplicationManualHubPage'));
const AdminPartnersPage = lazy(() => import('./pages/AdminPartnersPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const SyndicationDealsPage = lazy(() => import('./pages/SyndicationDealsPage'));
const SyndicationDealDetailPage = lazy(() => import('./pages/SyndicationDealDetailPage'));
const ApplicationPage = lazy(() => import('./pages/ApplicationPage'));
const ApplicationDetailPage = lazy(() => import('./pages/admin/pipeline/ApplicationDetailPage'));
const GrowthEnginePage = lazy(() => import('./pages/GrowthEnginePage'));
const TargetProspectsPage = lazy(() => import('./pages/TargetProspectsPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const CapitalRaisingPage = lazy(() => import('./pages/CapitalRaisingPage'));
const CapitalRaisingStage1Page = lazy(() => import('./pages/CapitalRaisingStage1Page'));
const CapitalRaisingStage2Page = lazy(() => import('./pages/CapitalRaisingStage2Page'));
const CapitalRaisingStage3Page = lazy(() => import('./pages/CapitalRaisingStage3Page'));
const CapitalRaisingStage4Page = lazy(() => import('./pages/CapitalRaisingStage4Page'));
const CapitalRaisingCompletePage = lazy(() => import('./pages/CapitalRaisingCompletePage'));
const TargetInvestorsPage = lazy(() => import('./pages/TargetInvestorsPage'));
const DataTradePage = lazy(() => import('./pages/DataTradePage'));
const PartnershipPage = lazy(() => import('./pages/PartnershipPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ColorTest = lazy(() => import('./pages/ColorTest'));
const DesignShowcase = lazy(() => import('./components/design-system/DesignShowcase'));
const InformationPage = lazy(() => import('./pages/InformationPage'));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));
const InvestorOnboardingPage = lazy(() => import('./pages/InvestorOnboardingPage'));
const VoluntasElevatePage = lazy(() => import('./pages/VoluntasElevatePage'));
const InternationalExpansionPage = lazy(() => import('./pages/InternationalExpansionPage'));
const GrowthPage = lazy(() => import('./pages/GrowthPage'));
const CapitalRaisePage = lazy(() => import('./pages/CapitalRaisePage'));
const CyberportPartnershipPage = lazy(() => import('./pages/CyberportPartnershipPage'));
const ReferralPartnersPage = lazy(() => import('./pages/ReferralPartnersPage'));
const PortfolioAccelerationPage = lazy(() => import('./pages/PortfolioAccelerationPage'));
const InvestorsPage = lazy(() => import('./pages/InvestorsPage'));
const ColdInvestorOutreachPage = lazy(() => import('./pages/ColdInvestorOutreachPage'));
const AlfredAIPage = lazy(() => import('./pages/AlfredAIPage'));
const GoGoPropPage = lazy(() => import('./pages/GoGoPropPage'));
const VolOceanPage = lazy(() => import('./pages/VolOceanPage'));
const VolOceanLaunchPage = lazy(() => import('./pages/VolOceanLaunchPage'));
const VolOceanTermsPage = lazy(() => import('./pages/VolOceanTermsPage'));
const VolOceanPrivacyPage = lazy(() => import('./pages/VolOceanPrivacyPage'));
const VolOceanRegulatoryPage = lazy(() => import('./pages/VolOceanRegulatoryPage'));

const VOLOCEAN_HOSTS = new Set(['volocean.com', 'www.volocean.com']);
const PUBLIC_VOLOCEAN_PATHS = new Set(['/', '/launch', '/volocean', '/volocean/launch', '/terms', '/privacy', '/regulatory']);

const isVolOceanHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  if (VOLOCEAN_HOSTS.has(host)) return true;
  // Dev: same SPA as production; show VOLOCEAN marketing routes at / (and related paths), not the legacy non-VolOcean landing.
  return host === 'localhost' || host === '127.0.0.1';
};

const normalizePathname = (pathname: string) => {
  if (pathname === '/') return pathname;
  return pathname.replace(/\/+$/, '');
};

const isPublicVolOceanPath = (pathname: string) => {
  const normalized = normalizePathname(pathname);
  // Always allow /volocean/* paths to render the VolOcean router for local testing
  if (normalized.startsWith('/volocean')) return true;
  return isVolOceanHost() && PUBLIC_VOLOCEAN_PATHS.has(normalized);
};

// Protected Route wrapper
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'client' | 'admin';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <VoloceanLoader size="lg" message="Authenticating..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const viewerRedirect = resolveTargetViewerProtectedRedirect(user.role, location.pathname);
  if (
    viewerRedirect &&
    !(
      user.role === 'target_viewer' &&
      user.prospectViewerClientId &&
      isProspectViewerAllowedPath(location.pathname)
    )
  ) {
    return <Navigate to={viewerRedirect} replace />;
  }

  const prospectRedirect = resolveProspectViewerProtectedRedirect(user.role, location.pathname);
  if (prospectRedirect) {
    return <Navigate to={prospectRedirect} replace />;
  }

  if (user.role === 'client' && user.approvalStatus !== 'approved' && user.approved !== true) {
    return <WaitingForApproval />;
  }

  if (
    !user.role ||
    !['admin', 'super_admin', 'client', 'investor', 'target_viewer', 'prospect_viewer'].includes(user.role)
  ) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  if (
    user.role === 'client' &&
    canAccessAlfredAiTargetGuide(user.email) &&
    !isPathAllowedForAlfredGuideOnly(location.pathname)
  ) {
    return <Navigate to={ALFRED_AI_GUIDE_PATH} replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    const redirectMap: Record<string, string> = {
      client: getApprovedClientHomePath(user.email),
      admin: '/admin',
      super_admin: '/admin',
      investor: '/investor-onboarding',
      target_viewer: TARGET_VIEWER_HOME_PATH,
      prospect_viewer: PROSPECT_VIEWER_HOME_PATH,
    };
    return <Navigate to={redirectMap[user.role] || '/auth'} replace />;
  }

  return <>{children}</>;
};

/** prospect_viewer, staff preview, and dual-scope target_viewer may open /view/prospects. */
const ProspectViewerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <VoloceanLoader size="lg" message="Authenticating..." />
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  if (
    !isProspectViewerRole(user.role) &&
    !isAdminRole(user.role) &&
    !isStaffViewerEmail(user.email) &&
    !(isTargetViewerRole(user.role) && Boolean(user.prospectViewerClientId))
  ) {
    return (
      <Navigate
        to={getRoleHomePath(user.role, getApprovedClientHomePath(user.email))}
        replace
      />
    );
  }

  return <>{children}</>;
};

/** target_viewer and staff preview may open /view/targets. */
const TargetViewerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <VoloceanLoader size="lg" message="Authenticating..." />
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  if (
    !isTargetViewerRole(user.role) &&
    !isAdminRole(user.role) &&
    !isStaffViewerEmail(user.email)
  ) {
    return (
      <Navigate
        to={getRoleHomePath(user.role, getApprovedClientHomePath(user.email))}
        replace
      />
    );
  }

  return <>{children}</>;
};

// Public Route wrapper (redirects if authenticated)
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <VoloceanLoader size="lg" message="Loading..." />
      </div>
    );
  }

  if (user && user.role) {
    const searchParams = new URLSearchParams(location.search);
    const redirectUrl = searchParams.get('redirect');

    const viewerHome = resolveTargetViewerPublicRedirect(user.role, redirectUrl);
    if (viewerHome) {
      return <Navigate to={viewerHome} replace />;
    }

    const prospectHome = resolveProspectViewerPublicRedirect(user.role, redirectUrl);
    if (prospectHome) {
      return <Navigate to={prospectHome} replace />;
    }

    if (redirectUrl) {
      return <Navigate to={redirectUrl} replace />;
    }

    const redirectMap: Record<string, string> = {
      client: getApprovedClientHomePath(user.email),
      admin: '/admin',
      super_admin: '/admin',
      investor: '/investor-onboarding',
      target_viewer: TARGET_VIEWER_HOME_PATH,
      prospect_viewer: PROSPECT_VIEWER_HOME_PATH,
    };
    return <Navigate to={redirectMap[user.role] || '/auth'} replace />;
  }

  return <>{children}</>;
};

// Admin Protected Route wrapper
interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <VoloceanLoader size="lg" message="Verifying access..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/admin/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  if (isTargetViewerRole(user.role)) {
    return <Navigate to={TARGET_VIEWER_HOME_PATH} replace />;
  }

  if (isProspectViewerRole(user.role)) {
    return <Navigate to={PROSPECT_VIEWER_HOME_PATH} replace />;
  }

  if (user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to={`/admin/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  return <>{children}</>;
};

// Layout wrapper for routes with navigation
interface LayoutWrapperProps {
  children: React.ReactNode;
}

const LayoutWrapper: React.FC<LayoutWrapperProps> = ({ children }) => {
  const { pathname } = useLocation();
  const showNav = shouldShowNav(pathname);
  const showChat = shouldShowGlobalChat(pathname);

  return (
    <div className="min-h-screen bg-white">
      {showNav && (
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
        >
          Skip to main content
        </a>
      )}
      {showNav && <Navigation />}
      {showChat && <GlobalChatOverlay />}
      <main id="main-content" className="relative bg-white" tabIndex={-1}>
        <RouteErrorBoundary variant="page">
          <Suspense fallback={getRouteSkeletonFallback(pathname)}>
            {children}
          </Suspense>
        </RouteErrorBoundary>
      </main>
    </div>
  );
};

// App Router Component
const AppRouter: React.FC = () => {
  const useVolOceanRoot = isVolOceanHost();

  return (
    <>
      <ScrollToTop />
      <AnalyticsBootstrapper />
      <Routes>
        {/* Standalone pages without navigation */}
        <Route path="/design-system" element={<DesignShowcase />} />
        <Route path="/color-test" element={<ColorTest />} />
        <Route path="/datatrade-process-documentation-2025" element={<DataTradePage />} />
        <Route path="/Think_Global_Voluntas" element={<PartnershipPage />} />
        <Route path="/elevate" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-white">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <VoluntasElevatePage />
          </Suspense>
        } />
        <Route path="/international-expansion" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-white">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <InternationalExpansionPage />
          </Suspense>
        } />
        <Route path="/growth" element={
          <Suspense fallback={<MarketingHeroSuspenseFallback posterSrc={VOLOCEAN_ASSETS.growthHeroPoster} />}>
            <GrowthPage />
          </Suspense>
        } />
        <Route path="/capital-raise" element={
          <Suspense fallback={<MarketingHeroSuspenseFallback posterSrc={VOLOCEAN_ASSETS.capitalRaiseHeroPoster} />}>
            <CapitalRaisePage />
          </Suspense>
        } />
        <Route path="/cyberport-anz-launchpad" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-primary-900">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <CyberportPartnershipPage />
          </Suspense>
        } />
        <Route path="/partners" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-white">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <ReferralPartnersPage />
          </Suspense>
        } />
        <Route path="/portfolio" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-white">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <PortfolioAccelerationPage />
          </Suspense>
        } />
        <Route path="/investors" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-white">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <InvestorsPage />
          </Suspense>
        } />
        <Route path="/investor-outreach" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-white">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <ColdInvestorOutreachPage />
          </Suspense>
        } />
        <Route path="/cold-investor-outreach" element={<Navigate to="/investor-outreach" replace />} />
        <Route path="/volocean" element={
          useVolOceanRoot ? (
            <Navigate to="/" replace />
          ) : (
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
                <VoloceanLoader size="lg" message="Loading..." />
              </div>
            }>
              <VolOceanPage />
            </Suspense>
          )
        } />
        <Route path="/volocean/launch" element={
          useVolOceanRoot ? (
            <Navigate to="/launch" replace />
          ) : (
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
                <VoloceanLoader size="lg" message="Loading..." />
              </div>
            }>
              <VolOceanLaunchPage />
            </Suspense>
          )
        } />
        <Route path="/launch" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <VolOceanLaunchPage />
          </Suspense>
        } />
        <Route path="/alfred-ai-target-customer-guide" element={
          <ClientSpecificRoute
            allowedDomains={[...ALFRED_AI_GUIDE_ALLOWED_DOMAINS]}
            allowedEmails={[...ALFRED_AI_GUIDE_ALLOWED_EMAILS]}
          >
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-primary-900">
                <VoloceanLoader size="lg" message="Loading..." />
              </div>
            }>
              <AlfredAIPage />
            </Suspense>
          </ClientSpecificRoute>
        } />
        <Route path={STANDALONE_PAGE_ACCESS.gogoprop.route} element={
          <StandaloneCodeGate
            accessCode={STANDALONE_PAGE_ACCESS.gogoprop.accessCode}
            storageKey={STANDALONE_PAGE_ACCESS.gogoprop.storageKey}
            storageMode={STANDALONE_PAGE_ACCESS.gogoprop.storageMode}
            pageLabel="the GoGoProp workspace"
          >
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-primary-900">
                <VoloceanLoader size="lg" message="Loading..." />
              </div>
            }>
              <GoGoPropPage />
            </Suspense>
          </StandaloneCodeGate>
        } />

        {/* Public Routes */}
        <Route path="/" element={
          useVolOceanRoot ? (
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
                <VoloceanLoader size="lg" message="Loading..." />
              </div>
            }>
              <VolOceanPage />
            </Suspense>
          ) : (
            <LayoutWrapper><PublicRoute><LandingPage /></PublicRoute></LayoutWrapper>
          )
        } />
        <Route path="/app" element={<LayoutWrapper><PublicRoute><LandingPage /></PublicRoute></LayoutWrapper>} />
        <Route path="/auth" element={<LayoutWrapper><PublicRoute><AuthPage /></PublicRoute></LayoutWrapper>} />
        {/* Public route — terminates the admin-issued passwordless sign-in invite. */}
        {/* Intentionally NOT wrapped in PublicRoute: the email link must be processed even if the user is signed in elsewhere. */}
        <Route path="/auth/claim" element={<LayoutWrapper><AuthClaimPage /></LayoutWrapper>} />
        <Route path="/auth/viewer-claim" element={<LayoutWrapper><AuthViewerClaimPage /></LayoutWrapper>} />
        <Route path="/auth/prospect-claim" element={<LayoutWrapper><AuthProspectClaimPage /></LayoutWrapper>} />
        <Route
          path="/view/targets"
          element={
            <LayoutWrapper>
              <TargetViewerRoute>
                <TargetViewerTargetsPage />
              </TargetViewerRoute>
            </LayoutWrapper>
          }
        />
        <Route
          path="/view/prospects"
          element={
            <LayoutWrapper>
              <ProspectViewerRoute>
                <ProspectViewerProspectsPage />
              </ProspectViewerRoute>
            </LayoutWrapper>
          }
        />
        <Route path="/admin/login" element={<LayoutWrapper><AdminLoginPage /></LayoutWrapper>} />
        <Route path="/reset-password" element={<LayoutWrapper><ResetPasswordPage /></LayoutWrapper>} />
        <Route path="/waiting-approval" element={<LayoutWrapper><WaitingForApproval /></LayoutWrapper>} />
        <Route path="/apply" element={<LayoutWrapper><ApplicationPage /></LayoutWrapper>} />
        <Route path="/information" element={<LayoutWrapper><InformationPage /></LayoutWrapper>} />

        {/* Investor Onboarding Route - No navigation bar (standalone page) */}
        <Route path="/investor-onboarding" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-white">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <InvestorOnboardingPage />
          </Suspense>
        } />

        {/* Protected Client Routes */}
        <Route path="/dashboard" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><ClientDashboard /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/profile" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><ClientProfilePage /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/growth-engine" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><GrowthEnginePage /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/documents" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><DocumentsPage /></ProtectedRoute></LayoutWrapper>} />

        {/* Capital Raising Routes - Client */}
        <Route path="/capital-raising" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><CapitalRaisingPage /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/capital-raising/stage-1" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><CapitalRaisingStage1Page /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/capital-raising/stage-2" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><CapitalRaisingStage2Page /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/capital-raising/stage-3" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><CapitalRaisingStage3Page /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/capital-raising/stage-4" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><CapitalRaisingStage4Page /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/capital-raising/complete" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><CapitalRaisingCompletePage /></ProtectedRoute></LayoutWrapper>} />
        <Route path="/capital-raising/target-investors" element={<LayoutWrapper><ProtectedRoute requiredRole="client"><TargetInvestorsPage /></ProtectedRoute></LayoutWrapper>} />

        {/* Settings */}
        <Route path="/settings" element={<LayoutWrapper><ProtectedRoute><UserSettingsPage /></ProtectedRoute></LayoutWrapper>} />

        {/* Admin Routes */}
        <Route path="/admin" element={<LayoutWrapper><AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/audit-logs" element={<LayoutWrapper><AdminProtectedRoute><AdminAuditLogsPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/pipeline" element={<LayoutWrapper><AdminProtectedRoute><ApplicationsPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/pipeline/:id" element={<LayoutWrapper><AdminProtectedRoute><ApplicationDetailPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/applications" element={<Navigate to="/admin/pipeline" replace />} />
        <Route path="/admin/applications/*" element={<Navigate to="/admin/pipeline" replace />} />
        <Route path="/admin/clients" element={<LayoutWrapper><AdminProtectedRoute><RedirectClientsDeliveryMode><AdminClientsPage /></RedirectClientsDeliveryMode></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/delivery" element={<LayoutWrapper><AdminProtectedRoute><AdminClientsPage lockedMode="delivery" /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/delivery/modular" element={<LayoutWrapper><AdminProtectedRoute><AdminClientsPage lockedMode="delivery" lockedView="modular" /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:id" element={<LayoutWrapper><AdminProtectedRoute><AdminClientDetailPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/investors" element={<LayoutWrapper><AdminProtectedRoute><RedirectInvestorsSyndicatesTab><AdminInvestorsPage /></RedirectInvestorsSyndicatesTab></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/syndicates" element={<LayoutWrapper><AdminProtectedRoute><AdminInvestorsPage lockedEntityMode="syndicates" /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/campaigns" element={<LayoutWrapper><AdminProtectedRoute><AdminCampaignsPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/partners" element={<LayoutWrapper><AdminProtectedRoute><AdminPartnersPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/leads" element={<LayoutWrapper><AdminProtectedRoute><AdminLeadsPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/volocean" element={<LayoutWrapper><AdminProtectedRoute><AdminVolOceanPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/pages" element={<LayoutWrapper><AdminProtectedRoute><AdminPublicPagesHub /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/application-manual" element={<LayoutWrapper><AdminProtectedRoute><ApplicationManualHubPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/analytics" element={<LayoutWrapper><AdminProtectedRoute><AdminAnalyticsPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/settings" element={<LayoutWrapper><AdminProtectedRoute><AdminSettingsPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/syndication-deals" element={<LayoutWrapper><AdminProtectedRoute><SyndicationDealsPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/syndication-deals/:dealId" element={<LayoutWrapper><AdminProtectedRoute><SyndicationDealDetailPage /></AdminProtectedRoute></LayoutWrapper>} />

        {/* Admin Capital Raising Routes */}
        <Route path="/admin/clients/:clientId/growth-engine" element={<LayoutWrapper><AdminProtectedRoute><GrowthEnginePage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:clientId/growth/target-prospects" element={<LayoutWrapper><AdminProtectedRoute><TargetProspectsPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:clientId/capital-raising" element={<LayoutWrapper><AdminProtectedRoute><CapitalRaisingPage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:clientId/capital-raising/stage-1" element={<LayoutWrapper><AdminProtectedRoute><CapitalRaisingStage1Page /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:clientId/capital-raising/stage-2" element={<LayoutWrapper><AdminProtectedRoute><CapitalRaisingStage2Page /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:clientId/capital-raising/stage-3" element={<LayoutWrapper><AdminProtectedRoute><CapitalRaisingStage3Page /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:clientId/capital-raising/stage-4" element={<LayoutWrapper><AdminProtectedRoute><CapitalRaisingStage4Page /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:clientId/capital-raising/complete" element={<LayoutWrapper><AdminProtectedRoute><CapitalRaisingCompletePage /></AdminProtectedRoute></LayoutWrapper>} />
        <Route path="/admin/clients/:clientId/capital-raising/target-investors" element={<LayoutWrapper><AdminProtectedRoute><TargetInvestorsPage /></AdminProtectedRoute></LayoutWrapper>} />

        {/* Legal Pages */}
        <Route path="/terms-and-conditions" element={<LayoutWrapper><TermsAndConditionsPage /></LayoutWrapper>} />
        <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />
        <Route path="/privacy" element={<LayoutWrapper><PrivacyPolicyPage /></LayoutWrapper>} />
        <Route path="/privacy-policy" element={<LayoutWrapper><PrivacyPolicyPage /></LayoutWrapper>} />
        <Route path="/regulatory" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
              <VoloceanLoader size="lg" message="Loading..." />
            </div>
          }>
            <VolOceanRegulatoryPage />
          </Suspense>
        } />

        {/* 404 Route */}
        <Route path="*" element={
          <LayoutWrapper>
            <AppCrashPage
              code="404"
              title="Page not found"
              message="That page does not exist."
              variant="page"
              showTryAgain={false}
              showSupport={false}
            />
          </LayoutWrapper>
        } />
      </Routes>
    </>
  );
};

const AppToaster: React.FC = () => (
  <Toaster
    position="top-right"
    toastOptions={{
      duration: 4000,
      style: {
        background: 'var(--color-white)',
        color: 'var(--color-primary-500)',
        borderRadius: '16px',
        border: '1px solid var(--color-primary-alpha-20)',
        padding: '16px',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 10%), 0 2px 4px -1px rgb(0 0 0 / 6%)',
        maxWidth: '400px',
      },
      success: {
        iconTheme: {
          primary: 'var(--color-success-500)',
          secondary: '#fff',
        },
      },
      error: {
        duration: 6000,
        iconTheme: {
          primary: 'var(--color-error-500)',
          secondary: '#fff',
        },
      },
      loading: {
        iconTheme: {
          primary: 'var(--color-primary-500)',
          secondary: '#fff',
        },
      },
    }}
  />
);

const VolOceanPublicRouter: React.FC = () => (
  <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
            <VoloceanLoader size="lg" message="Loading..." />
          </div>
        }>
          <VolOceanPage />
        </Suspense>
      } />
      <Route path="/launch" element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
            <VoloceanLoader size="lg" message="Loading..." />
          </div>
        }>
          <VolOceanLaunchPage />
        </Suspense>
      } />
      <Route path="/terms" element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
            <VoloceanLoader size="lg" message="Loading..." />
          </div>
        }>
          <VolOceanTermsPage />
        </Suspense>
      } />
      <Route path="/volocean/terms" element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
            <VoloceanLoader size="lg" message="Loading..." />
          </div>
        }>
          <VolOceanTermsPage />
        </Suspense>
      } />
      <Route path="/privacy" element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
            <VoloceanLoader size="lg" message="Loading..." />
          </div>
        }>
          <VolOceanPrivacyPage />
        </Suspense>
      } />
      <Route path="/volocean/privacy" element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
            <VoloceanLoader size="lg" message="Loading..." />
          </div>
        }>
          <VolOceanPrivacyPage />
        </Suspense>
      } />
      <Route path="/regulatory" element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
            <VoloceanLoader size="lg" message="Loading..." />
          </div>
        }>
          <VolOceanRegulatoryPage />
        </Suspense>
      } />
      <Route path="/volocean/regulatory" element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0F335B]">
            <VoloceanLoader size="lg" message="Loading..." />
          </div>
        }>
          <VolOceanRegulatoryPage />
        </Suspense>
      } />
      <Route path="/volocean" element={<Navigate to="/" replace />} />
      <Route path="/volocean/launch" element={<Navigate to="/launch" replace />} />
    </Routes>
    <AppToaster />
  </>
);

const AuthenticatedAppProviders: React.FC = () => (
  <AuthProvider>
    <CurrencyProvider>
      <ApplicationProvider>
        <ChatProvider>
          <AppRouter />
          <AppToaster />
        </ChatProvider>
      </ApplicationProvider>
    </CurrencyProvider>
  </AuthProvider>
);

const AppContent: React.FC = () => {
  const location = useLocation();

  if (isPublicVolOceanPath(location.pathname)) {
    return <VolOceanPublicRouter />;
  }

  return <AuthenticatedAppProviders />;
};

// Main App Component
const App: React.FC = () => {
  useEffect(() => {
    applyDemoDocumentTitle();
  }, []);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <RouteErrorBoundary>
        <AppContent />
      </RouteErrorBoundary>
    </Router>
  );
};

export default App;
